import { createCSVRow, generateCSVHeaders } from '../utils/csvContentUtils';

interface Position {
  [domain: string]: number | null;
}

interface KeywordGapItem {
  keyword?: string;
  volume?: number | string;
  kd?: number | string; 
  cpc?: number | string;
  intent?: string;
  positions?: Position;
  [key: string]: unknown;
}

interface KeywordGapData {
  data: KeywordGapItem[];
}

/**
 * Generate CSV content for keyword gap analysis data
 */
export const generateKeywordGapCSV = (data: KeywordGapData): string => {
  // Log entire data object structure for debugging
  console.log("=== CSV KEYWORD GAP GENERATOR DEBUGGING ===");
  console.log("[csvKeywordGapGenerator] Data type:", typeof data);
  console.log("[csvKeywordGapGenerator] Is Array?", Array.isArray(data));
  
  try {
    // Check if data exists and has the expected structure
    if (!data) {
      console.error("[csvKeywordGapGenerator] No data provided!");
      return "No data";
    }
    
    if (!data.data) {
      console.error("[csvKeywordGapGenerator] Data is missing 'data' property!", data);
      
      // Try to handle if data was passed directly as an array
      if (Array.isArray(data)) {
        console.log("[csvKeywordGapGenerator] Treating input as direct array");
        data = { data: data as unknown as KeywordGapItem[] };
      } else {
        return "Invalid data format";
      }
    }
    
    console.log("[csvKeywordGapGenerator] Generating CSV with data size:", data.data.length);
    
    // Log sample data to debug
    if (data.data.length > 0) {
      console.log("[csvKeywordGapGenerator] Sample data fields:", 
        Object.keys(data.data[0]).join(", "));
      console.log("[csvKeywordGapGenerator] First item:", 
        JSON.stringify(data.data[0], null, 2));
      
      if (data.data[0].positions) {
        console.log("[csvKeywordGapGenerator] Position domains:", 
          Object.keys(data.data[0].positions));
        console.log("[csvKeywordGapGenerator] First item positions:", 
          JSON.stringify(data.data[0].positions, null, 2));
          
        // Check each position value
        Object.entries(data.data[0].positions).forEach(([domain, position]) => {
          console.log(`[csvKeywordGapGenerator] Domain ${domain} position:`, position);
          console.log(`[csvKeywordGapGenerator] Position type:`, typeof position);
        });
      } else {
        console.error("[csvKeywordGapGenerator] WARNING: First item has no positions property!");
        console.log("[csvKeywordGapGenerator] Full item:", JSON.stringify(data.data[0]));
      }
    }
    
    // Get all domain names from the first keyword's positions
    const domains = data.data[0] && data.data[0].positions 
      ? Object.keys(data.data[0].positions) 
      : [];
      
    console.log("[csvKeywordGapGenerator] Found domains:", domains);
    
    // Create CSV headers with domains for gap analysis
    const headers = ["Keyword", "Volume", "KD", "CPC", "Intent"];
    
    // Add domain headers without "Position" suffix to match the table
    domains.forEach(domain => {
      headers.push(domain); // Remove "Position" suffix to match UI
    });
    
    console.log("[csvKeywordGapGenerator] CSV Headers:", headers);
    
    let csvContent = generateCSVHeaders(headers);
    
    // Add data rows
    data.data.forEach((item: KeywordGapItem, index) => {
      if (index === 0) {
        console.log("[csvKeywordGapGenerator] Processing first item positions:", item.positions);
      }
      
      // Format CPC as currency if it's a number
      const formattedCPC = typeof item.cpc === 'number' 
        ? `$${item.cpc.toFixed(2)}` // Add $ sign to match UI
        : item.cpc || '';
      
      // Format volume as number with commas
      const formattedVolume = typeof item.volume === 'number'
        ? item.volume.toLocaleString()
        : item.volume || '';
      
      const values = [
        item.keyword || '',
        formattedVolume,
        item.kd || '',
        formattedCPC,
        item.intent || ''
      ];
      
      // Add domain positions
      if (item.positions) {
        domains.forEach(domain => {
          // Check if positions data contains this domain
          if (domain in item.positions) {
            const positionValue = item.positions[domain];
            
            // Format position exactly like shown in the UI table
            const position = positionValue !== null && positionValue !== undefined
              ? positionValue 
              : '-'; // Use dash for null/undefined values
            
            if (index === 0) {
              console.log(`[csvKeywordGapGenerator] Domain ${domain} position:`, positionValue);
            }
            
            values.push(position);
          } else {
            // If domain not found in positions
            values.push('-');
            
            if (index === 0) {
              console.log(`[csvKeywordGapGenerator] Domain ${domain} not found in positions`);
            }
          }
        });
      } else {
        // If no positions data, add empty cells for each domain
        domains.forEach(domain => {
          values.push('-');
          
          if (index === 0) {
            console.log(`[csvKeywordGapGenerator] No positions data for domain ${domain}`);
          }
        });
      }
      
      csvContent += createCSVRow(values);
    });
    
    return csvContent;
  } catch (error) {
    console.error("[csvKeywordGapGenerator] Error generating CSV:", error);
    return "Error generating CSV";
  }
};

